import type { QuestDetail } from "Infrastructure/Api/Api";
import { UserQuestProgress } from "Infrastructure/Api/Api";
import { SideDrawer } from "Infrastructure/Components/Drawers/SideDrawer";
import { Button } from "Infrastructure/Components/Forms/Buttons/Button";
import { ReadOnlyRichTextInput } from "Infrastructure/Components/Forms/Inputs/ReadOnlyRichTextInput";
import { CreateQuestForm } from "Infrastructure/Components/Forms/QuestForms/CreateEditQuestForm";
import { useAuth } from "Infrastructure/Contexts/AuthContext";
import { formatDate } from "Infrastructure/Helpers/dateFormatters";
import { useDateFnsFormatString } from "Infrastructure/Hooks/DateFns/useDateFnsFormatString";
import { useRole } from "Infrastructure/Hooks/useRole";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useUserQuestMutation } from "Requests/Queries/useQuestRequestMutations";
import { useUserQuestQuery } from "Requests/Queries/useUserQuestQuery";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CalendarPlus2,
  CalendarX,
  CircleCheck,
  CirclePlay,
  Coins,
  type LucideIcon,
  NotebookPen,
  Tag,
  Type,
  Users,
} from "lucide-react";
import type { FC, ReactNode } from "react";
import { useState } from "react";

interface DetailViewProps {
  quest: QuestDetail | null;
  open: boolean;
  onClose: () => void;
}

type FieldValue = string | number | null | undefined;

interface FieldConfig {
  id: string;
  icon: LucideIcon;
  translationKey: keyof typeof Resources.Pages.DetailQuest.Fields;
  getValue: (quest: QuestDetail) => FieldValue;
  format?: "date" | "reward" | "type" | "description";
  layout?: "full" | "half";
  group?: string;
}

const fieldConfigs: FieldConfig[] = [
  {
    id: "name",
    icon: Type,
    translationKey: "Name",
    getValue: (q) => q.name,
    layout: "full",
  },
  {
    id: "type",
    icon: Tag,
    translationKey: "Type",
    getValue: (q) => q.type,
    format: "type",
    layout: "full",
  },
  {
    id: "rewardCoins",
    icon: Coins,
    translationKey: "Reward",
    getValue: (q) => q.rewardCoins,
    format: "reward",
    layout: "half",
    group: "reward",
  },
  {
    id: "maxParticipants",
    icon: Users,
    translationKey: "MaxParticipants",
    getValue: (q) => q.maxParticipants,
    layout: "half",
    group: "reward",
  },
  {
    id: "currentParticipants",
    icon: CirclePlay,
    translationKey: "Active",
    getValue: (q) => q.currentParticipantsCount,
    layout: "half",
    group: "participants",
  },
  {
    id: "completedParticipants",
    icon: CircleCheck,
    translationKey: "Completed",
    getValue: (q) => q.completedParticipantsCount,
    layout: "half",
    group: "participants",
  },
  {
    id: "dateStart",
    icon: CalendarPlus2,
    translationKey: "Start",
    getValue: (q) => q.dateStart,
    format: "date",
    layout: "half",
    group: "dates",
  },
  {
    id: "dateExpiration",
    icon: CalendarX,
    translationKey: "Expiration",
    getValue: (q) => q.dateExpiration,
    format: "date",
    layout: "half",
    group: "dates",
  },
  {
    id: "description",
    icon: NotebookPen,
    translationKey: "Description",
    getValue: (q) => q.description,
    format: "description",
    layout: "full",
  },
];

interface FieldCardProps {
  children: ReactNode;
}

const FieldCard: FC<FieldCardProps> = ({ children }) => {
  const { colors, customSpacing, radius } = useTheme();

  return (
    <Card
      sx={{
        background: colors.background.primary,
        borderRadius: radius.xl,
        boxShadow: "none",
        border: `1px solid ${colors.border.primary}`,
      }}
    >
      <CardContent
        sx={{
          padding: `${customSpacing.xl} !important`,
          "&:last-child": { paddingBottom: `${customSpacing.xl} !important` },
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
};

interface QuestFieldProps {
  config: FieldConfig;
  quest: QuestDetail;
}

const QuestField: FC<QuestFieldProps> = ({ config, quest }) => {
  const { colors, customFontWeight, customSpacing } = useTheme();
  const { t } = useResource();
  const dateFormat = useDateFnsFormatString();

  const fieldLabel = t(
    Resources.Pages.DetailQuest.Fields[config.translationKey],
  );
  const fieldValue = config.getValue(quest);
  const isDescription = config.format === "description";

  const formatValue = () => {
    if (!fieldValue && config.translationKey === "Expiration") {
      return t(Resources.Tables.Quests.NoTimeLimit);
    }

    switch (config.format) {
      case "reward":
        return (
          <Box
            component="span"
            sx={{
              background: `linear-gradient(90deg, ${colors.text.brandPrimary} 0%, ${colors.text.rewardGold} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            {fieldValue} {t(Resources.Pages.DetailQuest.RewardSuffix)}
          </Box>
        );

      case "date": {
        return formatDate(String(fieldValue), dateFormat);
      }

      case "type": {
        const typeKey =
          fieldValue as keyof typeof Resources.Pages.DetailQuest.Types;
        return t(Resources.Pages.DetailQuest.Types[typeKey]);
      }

      default:
        return fieldValue ?? "";
    }
  };

  return (
    <Box sx={{ flex: config.layout === "half" ? 1 : undefined }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ mb: isDescription ? customSpacing.xs : 0 }}
      >
        <config.icon
          size={16}
          color={colors.text.secondary}
          style={{ marginRight: customSpacing.md }}
        />
        <Typography
          variant="body2"
          color={colors.text.secondary}
          sx={{ mr: customSpacing.xs }}
        >
          {fieldLabel}
        </Typography>
        {!isDescription && (
          <Typography
            variant="body2"
            color={colors.text.primary}
            fontWeight={customFontWeight.medium}
            sx={{ ml: "auto" }}
          >
            {formatValue()}
          </Typography>
        )}
      </Stack>
      {isDescription && (
        <Box
          sx={{
            mt: customSpacing.xs,
            maxHeight: 195,
            overflowY: "auto",
            "& .ProseMirror": { p: 0, m: 0 },
            "& .ProseMirror > *:first-of-type": { mt: 0 },
            "& .ProseMirror > *:last-child": { mb: 0 },
          }}
        >
          <ReadOnlyRichTextInput value={String(fieldValue || "")} />
        </Box>
      )}
    </Box>
  );
};

export const QuestDetailCard: FC<DetailViewProps> = ({
  quest,
  open,
  onClose,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { customSpacing } = useTheme();
  const { t } = useResource();
  const { user } = useAuth();
  const { isGameMaster } = useRole();
  const mutation = useUserQuestMutation();
  const { data: userQuests } = useUserQuestQuery();

  const hasExistingRequest = userQuests?.items?.some(
    (uq) =>
      uq.questID === quest?.questID &&
      (uq.progress === UserQuestProgress.Requested ||
        uq.progress === UserQuestProgress.Active),
  );

  const handleClose = () => {
    setIsEditMode(false);
    onClose();
  };

  const handleRequestQuest = () => {
    if (!quest || !user) {
      return;
    }
    mutation.mutate({
      questID: quest.questID,
      progress: UserQuestProgress.Requested,
      dateStart: null,
      dateCompleted: null,
    });
  };

  const getButtonLabel = () => {
    if (mutation.isPending) {
      return t(Resources.Common.Loading);
    }
    if (hasExistingRequest) {
      return t(Resources.RequestCards.RequestDetail.Actions.RequestSent);
    }
    return t(Resources.RequestCards.RequestDetail.Actions.RequestQuest);
  };

  if (!quest) {
    return null;
  }
  const { fieldGroups } = fieldConfigs.reduce(
    (acc, field) => {
      if (
        field.id === "maxParticipants" &&
        (!quest.maxParticipants || quest.maxParticipants <= 0)
      ) {
        return acc;
      }

      if (field.layout === "full") {
        acc.fieldGroups.push([field]);
      } else {
        const groupKey = field.group || field.id;
        let group = acc.groupMap.get(groupKey);

        if (!group) {
          group = [];
          acc.groupMap.set(groupKey, group);
          acc.fieldGroups.push(group);
        }

        group.push(field);
      }

      return acc;
    },
    {
      fieldGroups: [] as FieldConfig[][],
      groupMap: new Map<string, FieldConfig[]>(),
    },
  );

  return (
    <>
      <SideDrawer
        open={open && !isEditMode}
        onClose={handleClose}
        title={t(Resources.Pages.DetailQuest.Title)}
        showEditButton={isGameMaster}
        onEdit={() => setIsEditMode(true)}
      >
        <Stack
          sx={{ flex: 1, minHeight: 0, flexDirection: "column" }}
          spacing={customSpacing.xl}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              pr: customSpacing.xs,
              pb: customSpacing.md,
            }}
          >
            <Stack spacing={customSpacing.md}>
              {fieldGroups.map((group) => {
                const groupKey = group.map((c) => c.id).join("-");
                return group.length === 1 ? (
                  <FieldCard key={groupKey}>
                    <QuestField config={group[0]} quest={quest} />
                  </FieldCard>
                ) : (
                  <Stack
                    key={groupKey}
                    direction="row"
                    spacing={customSpacing.md}
                    sx={{ width: "100%" }}
                  >
                    {group.map((config) => (
                      <Box key={config.id} sx={{ flex: 1 }}>
                        <FieldCard>
                          <QuestField config={config} quest={quest} />
                        </FieldCard>
                      </Box>
                    ))}
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              gap: customSpacing.md,
              flexShrink: 0,
            }}
          >
            <Button
              type="secondary"
              label={t(Resources.Common.Close)}
              onClick={handleClose}
            />
            {!isGameMaster && (
              <Button
                type="primary"
                label={getButtonLabel()}
                onClick={handleRequestQuest}
                disabled={mutation.isPending || hasExistingRequest}
              />
            )}
          </Stack>
        </Stack>
      </SideDrawer>

      <CreateQuestForm
        mode="edit"
        quest={quest}
        open={isEditMode}
        onClose={() => setIsEditMode(false)}
        onSuccess={() => {
          setIsEditMode(false);
          onClose();
        }}
      />
    </>
  );
};
